import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import {
  getGenerationLogsByProject,
  getTemplateSetsByProject,
  type GenerationLog,
  type TemplateSet,
} from '@/lib/api';
import {
  History,
  Download,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  CheckCircle,
} from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { toast } from 'sonner';

function downloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  );
}

function LogRow({ log, templateSetName }: { log: GenerationLog; templateSetName: string }) {
  const [expanded, setExpanded] = useState(false);
  const hasWarnings = log.warnings && log.warnings.length > 0;
  const hasFallbacks = log.fallbacks_used && log.fallbacks_used.length > 0;
  const hasDetails = hasWarnings || hasFallbacks;

  return (
    <div className="border-b border-border last:border-0">
      <div className="grid grid-cols-[1fr_120px_220px_80px_56px] gap-4 px-4 py-3 items-center">
        {/* Template + date */}
        <div>
          <div className="font-medium text-sm text-foreground">{templateSetName}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{formatDate(log.generated_at)}</div>
        </div>

        {/* Language */}
        <div>
          <Badge variant="outline" className="text-xs font-mono uppercase">
            {log.template_set_language_code}
          </Badge>
        </div>

        {/* Totals */}
        <div className="flex gap-3 text-xs text-muted-foreground">
          <span>
            <span className="text-foreground font-medium">{log.totals?.campaigns ?? 0}</span> camp.
          </span>
          <span>
            <span className="text-foreground font-medium">{log.totals?.ad_groups ?? 0}</span> ag.
          </span>
          <span>
            <span className="text-foreground font-medium">{log.totals?.ads ?? 0}</span> ads
          </span>
          <span>
            <span className="text-foreground font-medium">{log.totals?.keywords ?? 0}</span> kw.
          </span>
        </div>

        {/* Status */}
        <div>
          {hasWarnings ? (
            <span className="flex items-center gap-1 text-xs text-amber-400">
              <AlertTriangle className="size-3" />
              {log.warnings.length}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-emerald-400">
              <CheckCircle className="size-3" />
              Clean
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5">
          {log.output_blob && (
            <button
              onClick={() => downloadCSV(log.output_blob!, log.output_filename || 'export.csv')}
              title="Re-download CSV"
              className="p-1.5 rounded text-muted-foreground hover:text-primary transition-colors"
            >
              <Download className="size-3.5" />
            </button>
          )}
          {hasDetails && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 rounded text-muted-foreground hover:text-foreground transition-colors"
            >
              {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && hasDetails && (
        <div className="px-4 pb-4 space-y-3">
          {hasWarnings && (
            <div className="bg-amber-500/5 border border-amber-500/15 rounded-md p-3">
              <div className="text-xs font-medium text-amber-400 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="size-3" />
                Warnings ({log.warnings.length})
              </div>
              <div className="space-y-1">
                {log.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{w.element_type}</span> {w.element_id}: {w.message}
                  </p>
                ))}
              </div>
            </div>
          )}
          {hasFallbacks && (
            <div className="bg-accent/30 rounded-md p-3">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Fallbacks used ({log.fallbacks_used.length})
              </div>
              <div className="space-y-1">
                {log.fallbacks_used.map((f, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{f.element_type}</span> {f.slot_or_item_id}: variant{' '}
                    {f.variant_level} used <span className="font-medium text-foreground">{f.count}×</span>
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GenerationLogsView() {
  const { projectId } = useParams<{ projectId: string }>();
  const [logs, setLogs] = useState<GenerationLog[]>([]);
  const [templateSets, setTemplateSets] = useState<TemplateSet[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    Promise.all([getGenerationLogsByProject(projectId), getTemplateSetsByProject(projectId)])
      .then(([logsData, tsData]) => {
        setLogs(
          logsData.sort(
            (a, b) => new Date(b.generated_at).getTime() - new Date(a.generated_at).getTime()
          )
        );
        setTemplateSets(tsData);
      })
      .catch(() => toast.error('Failed to load generation logs'))
      .finally(() => setIsLoading(false));
  }, [projectId]);

  const getTemplateName = (id: string | null) => {
    if (!id) return 'Quick Generate';
    return templateSets.find((ts) => ts.id === id)?.name ?? id.slice(0, 8) + '…';
  };

  return (
    <div className="app-enter">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display text-xl font-bold text-foreground flex items-center gap-2">
            <History className="size-5 text-primary" />
            Generation Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            History of all CSV exports for this project.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <History className="size-10 text-muted-foreground/20 mb-3" />
          <h3 className="font-medium text-foreground text-sm mb-1">No exports yet</h3>
          <p className="text-xs text-muted-foreground max-w-xs">
            Each time you generate and download a CSV from the Generate tab, a record will appear here.
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_120px_220px_80px_56px] gap-4 px-4 py-2.5 bg-accent/30 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <div>Template Set</div>
            <div>Language</div>
            <div>Output</div>
            <div>Status</div>
            <div />
          </div>
          {logs.map((log) => (
            <LogRow key={log.id} log={log} templateSetName={getTemplateName(log.template_set_id)} />
          ))}
        </div>
      )}
    </div>
  );
}
